import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  RESOURCE_VISIBILITY,
  normalizeResourceVisibility,
  type ResourceVisibility,
} from 'types/visibility';

type VisibilityBadgeProps = {
  value?: ResourceVisibility | string | null;
  className?: string;
  uppercase?: boolean;
};

const VISIBILITY_BADGE_LABELS: Record<
  ResourceVisibility,
  { labelKey: string; labelFallback: string }
> = {
  [RESOURCE_VISIBILITY.PRIVATE]: {
    labelKey: 'PRIVATE',
    labelFallback: 'Private',
  },
  [RESOURCE_VISIBILITY.PUBLIC]: {
    labelKey: 'PUBLIC',
    labelFallback: 'Public',
  },
};

export function VisibilityBadge({
  value,
  className,
  uppercase = true,
}: VisibilityBadgeProps) {
  const { t } = useTranslation();
  const visibility = normalizeResourceVisibility(value ?? DEFAULT_RESOURCE_VISIBILITY);
  const meta = VISIBILITY_BADGE_LABELS[visibility];
  const label = t(meta.labelKey) || meta.labelFallback;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium tracking-wide',
        uppercase && 'uppercase',
        visibility === RESOURCE_VISIBILITY.PUBLIC
          ? 'border-[#dbeafe] bg-[#eff6ff] text-[#1d4ed8] dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
          : 'border-[#fde68a] bg-[#fffbeb] text-[#b45309] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300',
        className
      )}
    >
      {label}
    </span>
  );
}

export type { VisibilityBadgeProps };
